/** @jsxImportSource @emotion/react */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { Box, Button, Grid } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useCommonModal } from 'hooks/useCommonModal';
import { DefaultProps } from '../MdIvesCmpuRegPage';
import CustomRadio from 'components/inputs/CustomRadio';
import { MdIvesPrjInfo, MdModelSpecInfo } from 'models/md/MdIves';
import { findMdIvesMdlInfoList, findMdPrjInfo } from 'apis/md/MdProject';
import {
  getMdIvestMdlEquipmentReviewResult,
  getMdIvestMdlSpecComparisonData,
} from 'apis/md/MdInvestResult';
import {
  MdIvestMdlEquipmentReviewResult,
  MdIvestMdlSpecComparison,
} from 'models/md/MdInvestResult';
import ModelSpecComparisonTable from 'pages/md/invest/regist/result/SpecComparisonTable';
import InvestmentCostSummary from './result/InvestmentCostSummary';
import EquipmentReviewTable from './result/EquipmentReviewTable';
import InvestmentItemsRanking from './result/InvestmentItemsRanking';
import OtherSiteCostSummary from './result/OtherSiteCostSummary';
import InvestmentReviewSummary from './result/InvestmentReviewSummary';
import CommonSendEmailModal from 'components/modals/common/CommonSendEmailModal';
import { CommonUtil } from 'utils/CommonUtil';
import { ComboBox } from 'components/selects/ComboBox';
import { Code, fileTypeList } from 'models/common/CommonCode';
import { downloadExcelTemplatesPost } from 'apis/common/Excel';
import { FileTypeName } from 'models/common/Common';
import { useNavigate } from 'react-router-dom';

const IvesResultAnlsPage = forwardRef(
  ({ elmPrjId, ivesMdlId, isEditable = true }: DefaultProps, ref: React.Ref<any>) => {
    const equipmentReviewRef = useRef<any>();
    const costSummaryRef = useRef<any>();
    const rankRef = useRef<any>();
    const otherSiteRef = useRef<any>();
    const reviewRef = useRef<any>();
    const { t } = useTranslation();
    const { openMessageBar } = useMessageBar();
    const { openCommonModal } = useCommonModal();
    const { openLoading, isLoading } = useLoading();
    const navigate = useNavigate();

    const [selectFileType, setSelectFileType] = useState<string>();
    const excelRef = useRef<HTMLDivElement>(null);
    const reportRef = useRef<HTMLDivElement | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const [prjInfo, setPrjInfo] = useState<MdIvesPrjInfo>();
    const [modelList, setModelList] = useState<MdModelSpecInfo[]>([]);
    const [selectedModel, setSelectedModel] = useState<MdModelSpecInfo>({});

    const [comparisonData, setComparisonData] = useState<MdIvestMdlSpecComparison[]>([]);
    const [equipmentReviewData, setEquipmentReviewData] = useState<
      MdIvestMdlEquipmentReviewResult[]
    >([]);

    // [고도화] 메일 공유 팝업추가
    const [isOpenEmailModal, setOpenEmailModal] = useState<boolean>(false);
    const [conditionSendMail, setConditionSendMail] = useState<any>();

    const initCodes = () => {
      //
    };

    const isModify = () => {
      return !!selectedModel?.ivesMdlId && !selectedModel.ivesMdlId.includes('_NEW_');
    };

    const getData = async () => {
      // 상단 Radio 버튼 List 조회
      const modelList = await findMdIvesMdlInfoList({ elmPrjId });
      setModelList(modelList);
      if (modelList.length > 0) {
        if (ivesMdlId) {
          setSelectedModel(modelList.find((o) => o.ivesMdlId === ivesMdlId) ?? modelList[0]);
        } else {
          setSelectedModel(modelList.find((o) => o.cfmYn === 'Y') ?? modelList[0]);
        }
      }
    };

    const getSpecComparisonData = async () => {
      const comparisonData = await getMdIvestMdlSpecComparisonData(
        elmPrjId,
        selectedModel?.ivesMdlId
      );
      setComparisonData(comparisonData);
    };

    /*const getEquipmentReviewData = async () => {
    const equipmentReviewData = await getMdIvestMdlEquipmentReviewResult(
      elmPrjId,
      selectedModel?.ivesMdlId || ''
    );
    setEquipmentReviewData(equipmentReviewData);
  };*/

    const btnList = () => {
      //
    };

    const downloadExcelTemplate = useCallback(async () => {
      openLoading(true);
      downloadExcelTemplatesPost(
        FileTypeName.TPL_MD_IVES_RESULT,
        elmPrjId,
        [],
        selectedModel?.ivesMdlId
      ).finally(() => openLoading(false));
    }, [elmPrjId, selectedModel?.ivesMdlId]);

    const handleDownload = useCallback(async () => {
      if (selectFileType) {
        switch (selectFileType) {
          case 'PDF':
            openLoading(true);
            await CommonUtil.downloadElementAsPdf(reportRef.current, {
              fileName: '분석결과리포트',
              dateSuffixType: 'datetime',
              scale: 2,
              marginMm: 15,
              ignoreClassName: 'pdf-ignore',
            }).finally(() => openLoading(false));
            break;
          case 'EXL':
            await downloadExcelTemplate();
            break;
        }
      } else {
        openMessageBar({
          type: 'error',
          content: t('md.msg.다운로드 유형을 선택해주세요.', '다운로드 유형을 선택해주세요.'),
        });
      }
    }, [selectFileType, equipmentReviewData]);

    const handleMoveList = () => {
      navigate(`/md/ives-cmpu`);
    };

    const handleSave = useCallback(async () => {
      //if (!costSummaryRef.current?.validate()) return false;
      const tasks = [
        { name: 'equipment', ref: equipmentReviewRef },
        { name: 'cost', ref: costSummaryRef },
        { name: 'review', ref: reviewRef },
      ];

      let successCount = 0;
      let failCount = 0;
      let msg = '';

      for (const task of tasks) {
        try {
          const result = await task.ref.current?.save();
          if (result && result.successOrNot === 'Y') {
            successCount++;
          } else {
            failCount++;
            switch (task.name) {
              case 'cost':
                msg =
                  result.data && typeof result.data === 'string'
                    ? result.data
                    : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.');
                break;
              default:
                msg = t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.');
                break;
            }
            // break;
          }
        } catch (error) {
          failCount++;
          msg = t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.');
        }
      }

      // 최종 결과 메시지
      if (failCount > 0) {
        openMessageBar({
          type: 'error',
          content: msg,
        });
      } else if (successCount > 0) {
        for (const task of tasks) {
          await task.ref.current?.search(elmPrjId, selectedModel?.ivesMdlId);
        }
        openMessageBar({
          type: 'confirm',
          content: t('com.label.저장되었습니다.', '저장되었습니다.'),
        });
      }
    }, [elmPrjId, selectedModel?.ivesMdlId]);

    // [고도화] 메일 공유 버튼
    const handleSendMail = useCallback(() => {
      setConditionSendMail({
        id1: elmPrjId,
        id2: selectedModel.ivesMdlId,
        mailType: 'MDI_RLT_NOFT',
        mailTitle: '형개발 투자비 결과 공유 (메일발송)',
        mailSubjectTitle: '형개발 투자비',
        mailSubjectContent: prjInfo?.elmPrjNm,
      });

      setOpenEmailModal(true);
    }, [elmPrjId, selectedModel.ivesMdlId, prjInfo?.elmPrjNm]);

    const btnChgModel = (ivesMdlId) => {
      const selected = modelList.find((o) => o.ivesMdlId === ivesMdlId);
      setSelectedModel({ ...selected });
    };

    const btnDeleteModel = (item: MdModelSpecInfo) => {
      //
    };

    const handleOnChange = (name: string, value: any) => {
      //
    };

    useImperativeHandle(ref, () => ({
      isDirty: () => false,
    }));

    useEffect(() => {
      initCodes();
    }, []);

    useEffect(() => {
      getData();
      if (elmPrjId) {
        findMdPrjInfo(elmPrjId)
          .then((res) => {
            if (res.data && res.successOrNot === 'Y') {
              setPrjInfo({
                ...res.data,
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t(
                'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
                '요청 정보 조회 중 오류가 발생했습니다.'
              ),
            });
          });
      }
    }, [elmPrjId, ivesMdlId]);

    useEffect(() => {
      if (elmPrjId && selectedModel.ivesMdlId) {
        getSpecComparisonData();
        /*getEquipmentReviewData();*/
      }
    }, [elmPrjId, selectedModel.ivesMdlId]);

    return (
      <>
        <Box
        /*sx={{
          width: '210mm',
          minHeight: '297mm',
          mx: 'auto',
          p: '18mm',
          backgroundColor: '#ffffff',
          color: '#111827',
          boxSizing: 'border-box',
          border: '1px solid #e5e7eb',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        }}*/
        >
          <SubTitleLayout>
            <div>
              {modelList.map((o, i) => {
                return (
                  <CustomRadio
                    key={i}
                    id={`ivesResult-radio${i}`}
                    label={o.mdlNm || ''}
                    name="version"
                    checked={o.ivesMdlId === selectedModel?.ivesMdlId}
                    onChange={(e) => btnChgModel(o.ivesMdlId)}
                    deleteDisabled
                    onDelete={() => btnDeleteModel(o)}
                  />
                );
              })}
            </div>
            <GlobalBtnGroup className="pdf-ignore excel-ignore">
              <div style={{ width: '80px', marginRight: '4px' }}>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={fileTypeList}
                  onChange={(value) => {
                    setSelectFileType(value);
                  }}
                />
              </div>
              <Button
                css={IconButton.button}
                className="icoNone"
                disableRipple
                onClick={handleDownload}
                disabled={!isModify() || isDownloading}
              >
                {t('com.button.인쇄', '인쇄')}
              </Button>
              <Button
                css={IconButton.button}
                className="icoNone"
                disableRipple
                disabled={!isModify()}
                onClick={handleSendMail}
              >
                {t('com.button.전달', '전달')}
              </Button>
              <Button
                css={IconButton.button}
                className="icoNone"
                disableRipple
                onClick={handleMoveList}
              >
                {t('com.button.목록', '목록')}
              </Button>
              {isEditable && (
                <>
                  <Button
                    css={IconButton.button}
                    className="save"
                    disabled={!isModify() || isLoading}
                    onClick={handleSave}
                  >
                    {t('com.label.저장', '저장')}
                  </Button>
                </>
              )}
            </GlobalBtnGroup>
          </SubTitleLayout>
          <div
            ref={(el) => {
              (excelRef as any).current = el;
              (reportRef as any).current = el;
            }}
            className={'pdf-target excel-export-mode'}
          >
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('md.label.형개발 투자 검토 결과 요약', '형개발 투자 검토 결과 요약')}</h3>
              </SubTitleGroup>
            </SubTitleLayout>
            <Grid container>
              <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
                {/* 모델 주요 사양 비교 */}
                <ModelSpecComparisonTable data={comparisonData} />
                <SubTitleLayout>
                  <SubTitleGroup>
                    <h3>
                      {t(
                        'md.label.생산 대상 설비 적합 여부 점검 결과',
                        '생산 대상 설비 적합 여부 점검 결과'
                      )}
                    </h3>
                  </SubTitleGroup>
                </SubTitleLayout>
                <EquipmentReviewTable
                  ref={equipmentReviewRef}
                  elmPrjId={elmPrjId}
                  ivesMdlId={selectedModel?.ivesMdlId}
                  data={equipmentReviewData}
                  setData={setEquipmentReviewData}
                  isReadOnly={!isEditable}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
                <>
                  {/* 투자비 요약 */}
                  <InvestmentCostSummary
                    ref={costSummaryRef}
                    elmPrjId={elmPrjId}
                    ivesMdlId={selectedModel?.ivesMdlId}
                    isReadOnly={!isEditable}
                  />
                </>

                <div style={{ marginTop: '24px' }}>
                  {/* 투자항목별 Top 10 */}
                  <InvestmentItemsRanking
                    ref={rankRef}
                    elmPrjId={elmPrjId}
                    ivesMdlId={selectedModel?.ivesMdlId}
                  />
                </div>

                <div style={{ marginTop: '24px' }}>
                  {/* 타 Site 투자비 반영 내역 */}
                  <OtherSiteCostSummary
                    ref={otherSiteRef}
                    elmPrjId={elmPrjId}
                    ivesMdlId={selectedModel?.ivesMdlId}
                  />
                </div>

                <div style={{ marginTop: '24px' }}>
                  {/* 형개발 투자비 검토 결과 */}
                  <InvestmentReviewSummary
                    ref={reviewRef}
                    elmPrjId={elmPrjId}
                    ivesMdlId={selectedModel?.ivesMdlId}
                    isReadOnly={!isEditable}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Box>
        {isOpenEmailModal && (
          <CommonSendEmailModal
            open={isOpenEmailModal}
            close={() => setOpenEmailModal(false)}
            condition={conditionSendMail}
          />
        )}
      </>
    );
  }
);

IvesResultAnlsPage.displayName = 'IvesResultAnlsPage';

export default IvesResultAnlsPage;
